/**
 * Apify 지표 프론트엔드 더미 데이터
 *
 * 프록시 서버 없이 UI 를 확인할 수 있도록, 핸들 문자열을 시드로
 * 재현 가능한 가짜 지표를 생성합니다. 프록시 /api/collect 의 results 항목과
 * 동일한 형태를 반환합니다.
 */

function seedFrom(str) {
  let h = 2166136261;
  for (let i = 0; i < String(str).length; i++) {
    h ^= String(str).charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function rng(seed) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const PROFILE_URL = {
  instagram: (h) => `https://www.instagram.com/${h}/`,
  tiktok: (h) => `https://www.tiktok.com/@${h}`,
  youtube: (h) => `https://www.youtube.com/@${h}`,
  x: (h) => `https://x.com/${h}`,
  facebook: (h) => `https://www.facebook.com/${h}`,
  xiaohongshu: (h) => `https://www.xiaohongshu.com/user/profile/${h}`,
  lemon8: (h) => `https://www.lemon8-app.com/@${h}`,
};

export function apifyMetricsDummyData(platform, rawHandle, limit = 12) {
  const handle = String(rawHandle || '').replace(/^@/, '').trim();
  const rand = rng(seedFrom(`${platform}:${handle}`));
  const DAY = 86400000;
  const now = Date.now();

  const followers = Math.floor(3000 + rand() * 900000);
  const baseViews = Math.floor(followers * (0.1 + rand() * 0.8));
  const engage = 0.02 + rand() * 0.08;

  const following = Math.floor(80 + rand() * 1500);
  let cursor = now - Math.floor(rand() * 3) * DAY;
  const recentViews = [];
  const dates = [];
  const durations = [];
  const sponsoredFlags = [];
  const recentPosts = [];
  let sumViews = 0;
  let sumLikes = 0;
  let sumComments = 0;
  let sumDuration = 0;
  const profileUrl = (PROFILE_URL[platform] || ((x) => x))(handle);
  for (let i = 0; i < limit; i++) {
    const variance = 0.5 + rand();
    const views = Math.floor(baseViews * variance);
    const likes = Math.floor(views * engage * (0.7 + rand() * 0.6));
    const comments = Math.floor(likes * (0.02 + rand() * 0.08));
    const duration = Math.floor(15 + rand() * 90); // 15~105초
    const sponsored = rand() < 0.25; // 약 25% 확률로 협찬
    cursor -= Math.floor(2 + rand() * 7) * DAY;
    recentViews.push(views);
    dates.push(cursor);
    durations.push(duration);
    sponsoredFlags.push(sponsored);
    // 게시물 단위 더미 (썸네일은 실제 URL이 없으므로 null → 프론트에서 그라데이션 타일)
    recentPosts.push({
      url: `${profileUrl}${profileUrl.endsWith('/') ? '' : '/'}p/mock${i + 1}`,
      thumbnail: null,
      postedAt: new Date(cursor).toISOString(),
      views,
      likes,
      comments,
      sponsored,
    });
    sumViews += views;
    sumLikes += likes;
    sumComments += comments;
    sumDuration += duration;
  }

  const avgViews = Math.round(sumViews / limit);
  const sortedViews = [...recentViews].sort((a, b) => a - b);
  const medianViews = Math.round(sortedViews[Math.floor(limit / 2)]);
  const maxViews = Math.max(...recentViews);
  const minViews = Math.min(...recentViews);
  const avgLikes = Math.round(sumLikes / limit);
  const avgComments = Math.round(sumComments / limit);
  const avgShares = Math.round(avgLikes * 0.03);
  const avgSaves = Math.round(avgLikes * 0.1);
  const engagementRate = Number(((avgLikes + avgComments + avgShares + avgSaves) / avgViews).toFixed(4));
  const likeRate = Number((avgLikes / avgViews).toFixed(4));
  const commentRate = Number((avgComments / avgViews).toFixed(4));
  const saveRate = Number((avgSaves / avgViews).toFixed(4));
  const shareRate = Number((avgShares / avgViews).toFixed(4));
  const viewVariance = recentViews.reduce((s, v) => s + (v - avgViews) ** 2, 0) / recentViews.length;
  const viewConsistency = Math.max(0, Number((1 - Math.sqrt(viewVariance) / avgViews).toFixed(3)));
  const spikeRatio = Number((maxViews / medianViews).toFixed(2));
  const avgDurationSec = Number((sumDuration / limit).toFixed(1));
  const followerToFollowing = Number((followers / following).toFixed(2));
  const posts30d = dates.filter((t) => now - t <= 30 * DAY).length;
  const posts60d = dates.filter((t) => now - t <= 60 * DAY).length;
  const posts90d = dates.filter((t) => now - t <= 90 * DAY).length;
  let gap = 0;
  const sorted = [...dates].sort((a, b) => b - a);
  for (let i = 0; i < sorted.length - 1; i++) gap += sorted[i] - sorted[i + 1];
  const avgIntervalDays = Number((gap / (sorted.length - 1) / DAY).toFixed(1));
  const lastPostedAt = new Date(Math.max(...dates)).toISOString();
  const daysSinceLastPost = Math.round((now - Math.max(...dates)) / DAY);

  // 협찬 vs 오가닉 조회수
  const spV = recentViews.filter((_, i) => sponsoredFlags[i]);
  const orV = recentViews.filter((_, i) => !sponsoredFlags[i]);
  const sponsoredCount = spV.length;
  const avgViewsSponsored = spV.length ? Math.round(spV.reduce((a, b) => a + b, 0) / spV.length) : null;
  const avgViewsOrganic = orV.length ? Math.round(orV.reduce((a, b) => a + b, 0) / orV.length) : null;
  const sponsoredViewRatio =
    avgViewsSponsored != null && avgViewsOrganic ? Number((avgViewsSponsored / avgViewsOrganic).toFixed(2)) : null;

  return {
    handle,
    ok: true,
    platform,
    profileUrl,
    source: 'mock',
    metrics: {
      sampleSize: limit,
      followers,
      following,
      followerToFollowing,
      verified: rand() < 0.3,
      isBusiness: rand() < 0.4,
      isSeller: rand() < 0.2,
      category: platform === 'instagram' ? 'Blogger' : null,
      bioContacts: rand() < 0.5 ? [{ channel: 'email', value: `${handle}@gmail.com` }] : [],
      avgViews,
      medianViews,
      maxViews,
      minViews,
      avgLikes,
      avgComments,
      avgShares,
      avgSaves,
      metricsBasis: 'views',
      engagementRate,
      spikeRatio,
      likeRate,
      commentRate,
      saveRate,
      shareRate,
      viewConsistency,
      avgDurationSec,
      sponsoredCount,
      sponsoredRatio: Number((sponsoredCount / limit).toFixed(3)),
      avgViewsSponsored,
      avgViewsOrganic,
      sponsoredViewRatio,
      posts30d,
      posts60d,
      posts90d,
      avgIntervalDays,
      lastPostedAt,
      daysSinceLastPost,
      recentViews,
      recentPosts,
    },
  };
}
